import React from 'react';
import { Platform } from 'react-native';
import Svg, { Circle, Path, Rect } from 'react-native-svg';

/**
 * Значки плеера. Рисованные, как {@link BellGlyph}: эмодзи и PNG не тонируются цветом темы,
 * а на тёмном размытом фоне плеера им нужен один ровный белый.
 */

type GlyphProps = { size?: number; color: string; strokeWidth?: number };

const hidden = Platform.OS === 'web' ? {} : { accessibilityElementsHidden: true };

function Frame({ size = 24, children }: { size?: number; children: React.ReactNode }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      {children}
    </Svg>
  );
}

/** Нота — кнопка плеера в шапке. Две восьмые под ребром: читается как «музыка» и в 20 px. */
export function NoteGlyph({ size, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path
        d="M9 17.5V5.6l10-2.1v11.9"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path d="M9 8.4l10-2.1" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <Circle cx={6.6} cy={17.6} r={2.5} fill="none" stroke={color} strokeWidth={strokeWidth} />
      <Circle cx={16.6} cy={15.5} r={2.5} fill="none" stroke={color} strokeWidth={strokeWidth} />
    </Frame>
  );
}

export function PlayGlyph({ size, color }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M8 5.2v13.6a1 1 0 0 0 1.5.86l11-6.8a1 1 0 0 0 0-1.72l-11-6.8A1 1 0 0 0 8 5.2Z" fill={color} />
    </Frame>
  );
}

export function PauseGlyph({ size, color }: GlyphProps) {
  return (
    <Frame size={size}>
      <Rect x={6} y={4.5} width={4} height={15} rx={1.3} fill={color} />
      <Rect x={14} y={4.5} width={4} height={15} rx={1.3} fill={color} />
    </Frame>
  );
}

export function NextGlyph({ size, color }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M3.5 6.1v11.8a.9.9 0 0 0 1.4.74L13 12.74a.9.9 0 0 0 0-1.48L4.9 5.36a.9.9 0 0 0-1.4.74Z" fill={color} />
      <Path d="M12 6.1v11.8a.9.9 0 0 0 1.4.74l8.1-5.9a.9.9 0 0 0 0-1.48l-8.1-5.9a.9.9 0 0 0-1.4.74Z" fill={color} />
    </Frame>
  );
}

export function PreviousGlyph({ size, color }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M20.5 6.1v11.8a.9.9 0 0 1-1.4.74L11 12.74a.9.9 0 0 1 0-1.48l8.1-5.9a.9.9 0 0 1 1.4.74Z" fill={color} />
      <Path d="M12 6.1v11.8a.9.9 0 0 1-1.4.74l-8.1-5.9a.9.9 0 0 1 0-1.48l8.1-5.9A.9.9 0 0 1 12 6.1Z" fill={color} />
    </Frame>
  );
}

export function ShuffleGlyph({ size, color, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path
        d="M3 7h3.2c1.6 0 3 .8 3.9 2.1l3.8 5.8c.9 1.3 2.3 2.1 3.9 2.1H21M3 17h3.2c1.6 0 3-.8 3.9-2.1M13.9 9.1c.9-1.3 2.3-2.1 3.9-2.1H21"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <Path d="M18.5 4.5 21 7l-2.5 2.5M18.5 14.5 21 17l-2.5 2.5" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function RepeatGlyph({ size, color, strokeWidth = 1.9, one = false }: GlyphProps & { one?: boolean }) {
  return (
    <Frame size={size}>
      <Path
        d="M4 11.5V10a3 3 0 0 1 3-3h13M20 12.5V14a3 3 0 0 1-3 3H4"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <Path d="M17.5 4.5 20 7l-2.5 2.5M6.5 14.5 4 17l2.5 2.5" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      {one ? <Path d="M11.2 10.6 12.6 9.8v4.4" fill="none" stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" /> : null}
    </Frame>
  );
}

export function SlidersGlyph({ size, color, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M6 4v16M12 4v16M18 4v16" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" opacity={0.45} />
      <Rect x={3.6} y={12.5} width={4.8} height={3} rx={1.5} fill={color} />
      <Rect x={9.6} y={6.5} width={4.8} height={3} rx={1.5} fill={color} />
      <Rect x={15.6} y={10} width={4.8} height={3} rx={1.5} fill={color} />
    </Frame>
  );
}

export function PlusGlyph({ size, color, strokeWidth = 2 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M12 5v14M5 12h14" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Frame>
  );
}

export function ChevronDownGlyph({ size, color, strokeWidth = 2.2 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="m6 9.5 6 6 6-6" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function ChevronLeftGlyph({ size, color, strokeWidth = 2.2 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="m14.5 6-6 6 6 6" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function ChevronRightGlyph({ size, color, strokeWidth = 2.2 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="m9.5 6 6 6-6 6" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function MoreGlyph({ size, color }: GlyphProps) {
  return (
    <Frame size={size}>
      <Circle cx={5.5} cy={12} r={1.8} fill={color} />
      <Circle cx={12} cy={12} r={1.8} fill={color} />
      <Circle cx={18.5} cy={12} r={1.8} fill={color} />
    </Frame>
  );
}

export function TrashGlyph({ size, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path
        d="M4.5 6.5h15M9.5 6.5V4.8c0-.7.5-1.3 1.2-1.3h2.6c.7 0 1.2.6 1.2 1.3v1.7M6.5 6.5l.8 12.2c.1 1 .9 1.8 1.9 1.8h5.6c1 0 1.8-.8 1.9-1.8l.8-12.2M10 10.5v6M14 10.5v6"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Frame>
  );
}

/** Список с плюсом — «добавить в плейлист». */
export function PlaylistAddGlyph({ size, color, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path d="M4 6.5h11M4 11.5h11M4 16.5h7M17.5 13.5v7M14 17h7" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Frame>
  );
}

export function MinusCircleGlyph({ size, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Circle cx={12} cy={12} r={8.5} fill="none" stroke={color} strokeWidth={strokeWidth} />
      <Path d="M8 12h8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Frame>
  );
}

export function PencilGlyph({ size, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Frame size={size}>
      <Path
        d="M4.5 19.5l.9-3.9L15.8 5.2a1.9 1.9 0 0 1 2.7 0l.3.3a1.9 1.9 0 0 1 0 2.7L8.4 18.6l-3.9.9ZM14 7l3 3"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Frame>
  );
}
